import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { API_BACKEND_URL } from "../../utils/envUtils";

/**
 * Versión desplegada de la web y del backend, al final de Settings. Es el commit corto
 * (lo que identifica el push) y su fecha. Se rellena sola en cada build: la web con
 * scripts/build.js y el backend con GET /api/healthcheck/version.
 */

interface BackendBuildInfo {
  commit: string | null;
  commitDate: string | null;
  builtAt: string | null;
  revision: string | null;
}

const formatDate = (iso: string | null | undefined, locale: string) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const describe = (
  commit: string | null | undefined,
  date: string | null | undefined,
  locale: string,
  fallback: string
) => {
  const parts = [commit, formatDate(date, locale)].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : fallback;
};

const AppVersion: React.FC = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [backend, setBackend] = useState<BackendBuildInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BACKEND_URL}/api/healthcheck/version`)
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        if (!cancelled && json?.data) setBackend(json.data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const locale = i18n.language || "es";
  const dev = t("settings.version.local");
  const web = describe(
    process.env.REACT_APP_BUILD_COMMIT,
    process.env.REACT_APP_BUILD_COMMIT_DATE || process.env.REACT_APP_BUILD_TIME,
    locale,
    dev
  );
  const api = backend
    ? describe(
        backend.commit ?? backend.revision,
        backend.commitDate ?? backend.builtAt,
        locale,
        dev
      )
    : "…";

  return (
    <Box sx={{ mt: 4, textAlign: "center" }}>
      <Typography
        variant="caption"
        component="p"
        sx={{ color: theme.palette.text.secondary, fontFamily: "Nunito" }}
      >
        {t("settings.version.web")} {web} — {t("settings.version.api")} {api}
      </Typography>
    </Box>
  );
};

export default AppVersion;
